import { useMemo } from 'react';

import { PAGES } from 'common/enums';
import type { User } from 'common/interfaces';

import { useIsUserHaveRole } from 'hooks/use-is-user-have-role';
import { useComponent } from 'hooks/use-component/use-component';
import { useIsProductAvailable } from 'hooks/use-is-product-available/use-is-product-available';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { ProductId } from 'hooks/react-query/product/get-user-product/get-user-product.props';
import { useReportDamageCustomLink } from 'hooks/custom-links/use-report-damage';
import { availabilityRollsForProduct } from 'pages/create-damage/utils';
import { userSelector } from 'selectors';

import { USER_ROLES } from '../../../constants';
import { createBaseMenu } from '../lib/create-base-menu';
import { fillMenuItemsWithRoles } from '../lib/fill-menu-items';
import { filterMenuItems } from '../lib/filter-menu-items';
import type { MainMenuItem } from '../side-menu-interfaces';
import { addCustomPages } from '../lib/add-custom-pages';
import { cloneDeepWithReactElements } from '@/utils/clone-deep-with-react-elements';

export const useMenuItems = () => {
  const { isAvailable: isShowCreateDamage } = useIsProductAvailable(
    ProductId.SelfManagedClaims,
    availabilityRollsForProduct[ProductId.SelfManagedClaims]
  );
  const { reportDamageCustomLink } = useReportDamageCustomLink();
  const { getIsPageAvailable } = useComponent();
  const isSiteAvailable: boolean = useTypedSelector(state => state.app.isSiteAvailable);
  const isDriver = useIsUserHaveRole(USER_ROLES.driver.id);
  const isAdmin = useIsUserHaveRole(USER_ROLES.administrator.id);
  const isAdminOrDriver = useIsUserHaveRole([USER_ROLES.driver.id, USER_ROLES.administrator.id]);
  const isCreateDamagePageAvailable = getIsPageAvailable(PAGES.CREATE_DAMAGE);
  const user: User = useTypedSelector(userSelector);
  const mainRoleId = user.mainRole?.id;

  const damageLinks = useMemo(() => {
    const damageLinks = [PAGES.DAMAGE, PAGES.DAMAGE_DETAILS];

    if (!isShowCreateDamage && !isAdminOrDriver) {
      damageLinks.push(PAGES.CREATE_DAMAGE);
    }

    return damageLinks;
  }, [isAdminOrDriver, isShowCreateDamage]);

  const baseMenu = useMemo<MainMenuItem[]>(
    () =>
      createBaseMenu({
        damageLinks,
        isCreateDamagePageAvailable,
        isDriver,
        reportDamageCustomLink,
        isAdmin
      }),
    [isCreateDamagePageAvailable, damageLinks, isDriver, reportDamageCustomLink, isAdmin]
  );

  const baseMenuWithCustomPages = useMemo(
    () => addCustomPages(cloneDeepWithReactElements(baseMenu), user),
    [baseMenu, user.customPages.length]
  );
  const filledMenu = useMemo(
    () => fillMenuItemsWithRoles(baseMenuWithCustomPages, user),
    [baseMenuWithCustomPages, user.appPages.length, user.customPages.length, user.schadenmeldenLink, mainRoleId]
  );
  const processedMenu = useMemo(
    () => filterMenuItems(filledMenu, { isDriver, isSiteAvailable, mainRoleId }),
    [filledMenu, isDriver, isSiteAvailable, mainRoleId]
  );

  return { menuItems: processedMenu };
};
